import { isBundledMobileApp, resolveMobileApiUrl } from '@/mobile/native/apiBase';
import { mapNetworkFetchError } from '@/mobile/native/mapNetworkFetchError';
import { loadMobileApiToken } from '@/mobile/native/mobileAuthToken';

const MOBILE_APP_HEADER = 'X-Engetel-Mobile-App';

function isApiPath(path: string): boolean {
  return path.startsWith('/api/') || path.includes('/api/');
}

function mapHttpError(status: number, body: string): string {
  if (status === 419) {
    return 'Sessão inválida no servidor. Saia e faça login novamente no app.';
  }

  if (status === 401) {
    return 'Sessão expirada. Faça login novamente.';
  }

  try {
    const json = JSON.parse(body) as {
      message?: string;
      errors?: Record<string, string[]>;
    };

    if (json.errors) {
      const first = Object.values(json.errors).flat().find(Boolean);
      if (first) {
        return first;
      }
    }

    if (json.message) {
      return json.message;
    }
  } catch {
    // not json
  }

  return body || `Erro HTTP ${status}`;
}

function readXsrfToken(): string {
  const prefix = 'XSRF-TOKEN=';
  const entry = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));

  if (!entry) {
    return '';
  }

  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return entry.slice(prefix.length);
  }
}

export async function mobileApiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  headers.set('X-Requested-With', 'XMLHttpRequest');

  if (!isBundledMobileApp()) {
    const xsrf = readXsrfToken();
    if (xsrf && !headers.has('X-XSRF-TOKEN')) {
      headers.set('X-XSRF-TOKEN', xsrf);
    }
  }

  if (isBundledMobileApp()) {
    headers.set(MOBILE_APP_HEADER, '1');

    const token = await loadMobileApiToken();

    if (isApiPath(path) && !token) {
      throw new Error('Faça login novamente para continuar.');
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    headers.delete('X-Inertia-Version');

    const response = await fetch(resolveMobileApiUrl(path), {
      ...init,
      headers,
      credentials: 'omit',
    }).catch((error) => {
      throw mapNetworkFetchError(error);
    });

    if (response.status === 419) {
      throw new Error(mapHttpError(419, await response.clone().text()));
    }

    return response;
  }

  return fetch(path, {
    ...init,
    headers,
    credentials: init.credentials ?? 'same-origin',
  });
}

export { mapHttpError as mapMobileApiHttpError };
